/* eslint-disable max-lines */
import { receiveNewKeyword } from 'actions/identifier';
import ToggleButton from 'components/ToggleButton';
import WarningMsg from 'components/WarningMsg';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  getActiveLanguageMatcher,
  getActiveLanguageMatcherId,
  getCompareMode,
  getLanguageMatcherTypes,
  getShouldDisableEdit,
  saveIdentifierRequestLoading,
  unsavedChanges,
} from 'selectors/identifier';
import { useSelector } from 'store';
import { languageMatcherNotificationMsgs } from 'utils/userNotificationMsgs';
import AddLanguageMatcherInput from '../AddLanguageMatcherInput';
import TermsInput from '../AddLanguageMatcherInput/TermsInput';
import ChooseCombinationType from '../ChooseCombinationType';
import DeleteMatcherButton from '../DeleteMatcherButton';
import LanguageMatcherHeader from '../LanguageMatcherHeader';

type LanguageMatcherType = {
  isSaved: boolean;
};

const AdjacentTokensForm: React.FC<LanguageMatcherType> = ({ isSaved }) => {
  const dispatch = useDispatch();

  const unsaved = useSelector(unsavedChanges);
  const activeLanguageMatcher = useSelector(getActiveLanguageMatcher);
  const activeLanguageMatcherId = useSelector(getActiveLanguageMatcherId);
  const isLoadingSaveIdentifier = useSelector(saveIdentifierRequestLoading);
  const languageMatcherTypes = useSelector(getLanguageMatcherTypes);
  const compareMode = useSelector(getCompareMode);
  const shouldDisable = useSelector(getShouldDisableEdit);

  const tokenExcludedFields = [
    'first_tokens',
    'first_token_pos',
    'tenses',
    'first_token_ent_types',
    'first_token_negated',
    'second_tokens',
    'second_token_pos',
    'second_token_tenses',
    'second_token_ent_types',
    'second_token_negated',
    'is_exclusion',
    'team_uuids',
    'not_team_uuids',
    'type',
    'status',
    'max_distance',
    'strict_ordering',
  ];

  useEffect(() => {
    if (activeLanguageMatcher && activeLanguageMatcher.isNew) {
      const input = document.getElementById('name');
      input?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLanguageMatcherId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleToggleClick = (field: string): void => {
    // @ts-ignore
    const value = !activeLanguageMatcher[field];

    dispatch(
      receiveNewKeyword({
        languageMatcherId: activeLanguageMatcherId,
        parentType: 'adjacent_tokens',
        data: {
          // @ts-ignore
          key: field,
          value,
        },
      })
    );

    dispatch(
      receiveNewKeyword({
        languageMatcherId: activeLanguageMatcherId,
        parentType: 'adjacent_tokens',
        data: {
          key: 'saved',
          value: false,
        },
      })
    );
  };

  if (Object.keys(languageMatcherTypes).length === 0) return null;

  return (
    <div className="flex flex-col pt-5 pl-10 pr-4 pb-6 text-body h-full">
      <div className="flex flex-row justify-end">
        <WarningMsg
          message={languageMatcherNotificationMsgs.unsavedChanges}
          showLoading={isLoadingSaveIdentifier}
          showWarning={unsaved}
          showSavedMsg={isSaved}
        />
      </div>
      <div className="-mt-4 flex flex-col gap-4 text-body h-full">
        <LanguageMatcherHeader />
        <div className="mt-6 flex flex-row items-center gap-3">
          <ToggleButton
            // @ts-ignore
            isOn={activeLanguageMatcher.is_exclusion as boolean}
            handleToggleClick={(): void => handleToggleClick('is_exclusion')}
            disabled={compareMode || shouldDisable}
          />
          <span>Exclude matches</span>
        </div>

        <div className="p-4 pt-2 flex flex-col gap-4 bg-litlingo-gray-1 bg-opacity-50 rounded-lg">
          <TermsInput
            languageMatcherId={activeLanguageMatcherId}
            type="adjacent_tokens"
            // @ts-ignore
            form="first_tokens"
          />
          <div className="flex flex-row gap-5">
            <div className="w-1/2">
              <AddLanguageMatcherInput
                languageMatcherId={activeLanguageMatcherId}
                type="adjacent_tokens"
                // @ts-ignore
                form="first_token_pos"
              />
            </div>
            <div className="w-1/4">
              <AddLanguageMatcherInput
                languageMatcherId={activeLanguageMatcherId}
                type="adjacent_tokens"
                // @ts-ignore
                form="tenses"
              />
            </div>
            <div className="w-1/4">
              <AddLanguageMatcherInput
                languageMatcherId={activeLanguageMatcherId}
                type="adjacent_tokens"
                // @ts-ignore
                form="first_token_ent_types"
              />
            </div>
          </div>
        </div>

        <div className="p-4 pt-2 flex flex-col gap-4 bg-litlingo-gray-1 bg-opacity-50 rounded-lg">
          <TermsInput
            languageMatcherId={activeLanguageMatcherId}
            type="adjacent_tokens"
            // @ts-ignore
            form="second_tokens"
          />
          <div className="flex flex-row gap-5">
            <div className="w-1/2">
              <AddLanguageMatcherInput
                languageMatcherId={activeLanguageMatcherId}
                type="adjacent_tokens"
                // @ts-ignore
                form="second_token_pos"
              />
            </div>
            <div className="w-1/4">
              <AddLanguageMatcherInput
                languageMatcherId={activeLanguageMatcherId}
                type="adjacent_tokens"
                // @ts-ignore
                form="second_token_tenses"
              />
            </div>
            <div className="w-1/4">
              <AddLanguageMatcherInput
                languageMatcherId={activeLanguageMatcherId}
                type="adjacent_tokens"
                // @ts-ignore
                form="second_token_ent_types"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-row gap-5">
          <div className="w-60">
            <AddLanguageMatcherInput
              languageMatcherId={activeLanguageMatcherId}
              type="adjacent_tokens"
              // @ts-ignore
              form="max_distance"
            />
          </div>
          <div className="flex flex-col gap-3">
            <span>Strict ordering</span>
            <div className="flex flex-row gap-4">
              <label htmlFor="no" className="flex flex-row items-center gap-2">
                <input
                  type="radio"
                  name="strict-ordering"
                  id="no"
                  value="no"
                  className="input-radio"
                  // @ts-ignore
                  checked={!activeLanguageMatcher.strict_ordering}
                  onChange={(): void => handleToggleClick('strict_ordering')}
                />
                No
              </label>
              <label htmlFor="yes" className="flex flex-row items-center gap-2">
                <input
                  type="radio"
                  name="strict-ordering"
                  id="yes"
                  value="yes"
                  className="input-radio"
                  // @ts-ignore
                  checked={activeLanguageMatcher.strict_ordering}
                  onChange={(): void => handleToggleClick('strict_ordering')}
                />
                <span>Yes</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4 mt-2">
          <TermsInput
            languageMatcherId={activeLanguageMatcherId}
            type="adjacent_tokens"
            // @ts-ignore
            form="team_uuids"
          />

          <TermsInput
            languageMatcherId={activeLanguageMatcherId}
            type="adjacent_tokens"
            // @ts-ignore
            form="not_team_uuids"
            useDisabledColor
          />
        </div>

        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-row items-center gap-15">
            <div className="flex flex-row items-center gap-3">
              <ToggleButton
                // @ts-ignore
                isOn={activeLanguageMatcher.first_token_negated as boolean}
                handleToggleClick={(): void => handleToggleClick('first_token_negated')}
                disabled={compareMode || shouldDisable}
              />
              <span>First Token Negated</span>
            </div>
            <div className="flex flex-row items-center gap-3">
              <ToggleButton
                // @ts-ignore
                isOn={activeLanguageMatcher.second_token_negated as boolean}
                handleToggleClick={(): void => handleToggleClick('second_token_negated')}
                disabled={compareMode || shouldDisable}
              />
              <span>Second Token Negated</span>
            </div>
          </div>
        </div>

        <div>
          <ChooseCombinationType
            activeLanguageMatcher={activeLanguageMatcher}
            excludedFields={tokenExcludedFields}
          />
        </div>

        <div className="mt-auto pt-6 flex flex-row justify-end">
          <DeleteMatcherButton
            text="Delete Matcher"
            setValue={(): void => {
              // moot
            }}
            setHighlightedLanguageMatcher={(): void => {
              // moot
            }}
            activeLanguageMatcher={activeLanguageMatcher}
          />
        </div>
      </div>
    </div>
  );
};

export default AdjacentTokensForm;
